import { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import RightChevron from "../../../../../src/assets/images/chevron-left-color.svg";

import { FINAL_ASSESSMENT_ALLOWED, CRE_FINAL_ASSESSMENT_RESULT } from "../../../../utils/cookiesmanager";
import { QueryKeys } from "../../../../utils/queryKeys";

import { BUTTON_VARIANTS } from "../../lms/constant";
import Button from "../../lms/ButtonComponent";
import Shimmer from "../../lms/ShimmerComponent";

import AssessmentResultModal from "../AssessmentResultModal";
import SubmitModal from "../SubmitModal";
import ExitAssessmentModal from "../ExitAssessmentModal";
import HeaderModule from "../ModuleHeaderComponent";
import CourseQuestionCards from "../CourseQuestionCards";

import { useGetFinalAssessmentQuestion } from "./queries";
import { useSavePostChapterAssessment } from "./mutation";

import styles from "./styles.module.scss";
import { useGetTranslatedText } from "../../lms/queries";
import { getTranslatedText } from "../../../../utils/getTranslatedText";

const FinalAssessmentQuestion = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeQuestion , setActiveQuestion] = useState(0)
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [animate] = useState(false);
  const [allowLeave , setAllowLeave] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(10 * 60);
  const [loader , setLoader] = useState(false);
  const [open , setOpen] = useState(false);
  const [openSubmitModal , setOpenSubmitModal] = useState(false);

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const languageid = localStorage.getItem("l_id") || 1;

  const { data: finalAssessmentQuestionResponse , isLoading , isFetching } =
    useGetFinalAssessmentQuestion({ languageid });
              
    const { data: translatedTextResponse } = useGetTranslatedText(languageid);
              
    const { data: translatedText = [] } = translatedTextResponse || {};

  const { mutate , isPending } = useSavePostChapterAssessment();

  const { response: postAssessmentChapterQuestionData } =
    finalAssessmentQuestionResponse || {};

  
  const queryClient = useQueryClient();  

  const handleNext = () => {
    const currentQuestion = postAssessmentChapterQuestionData?.[activeIndex];

    // block next if unanswered
    if (!selectedAnswers[currentQuestion?.id]) {
      toast.error("Please select an answer");
      return;
    }

    // last question → submit
    if (activeIndex === postAssessmentChapterQuestionData.length - 1) {
      setActiveQuestion((prev) => prev + 1);
      setOpenSubmitModal(true)
      return;
    }
    setActiveIndex((prev) => prev + 1);
    setActiveQuestion((prev) => prev + 1);
   
  };

  const submitQuestionAnswers = ()  => {
    setLoader(true);
      const payload = {
        questionArray: Object.entries(selectedAnswers).map(
          ([assQuestionID, assAnswerID]) => ({
            questionId:assQuestionID,
            optionId:assAnswerID,
          }),
        ),
      };

      mutate(payload, {
        onSuccess(data) {
          const { status, error , response } = data;

          if (!status) {
            throw new Error(error);
          }

          queryClient.invalidateQueries({
              queryKey: [QueryKeys.FINAL_ASSESSMENT_KEYS], // use your actual query key
          });

          localStorage.setItem(CRE_FINAL_ASSESSMENT_RESULT,JSON.stringify(response));
          setOpen(true);
          setOpenSubmitModal(false);
        },
        onError(error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          toast(errorMessage, { type: "error" });
        },
        onSettled() {
          setLoader(false);
        },
      });
      return;
  }

  useEffect(() => {
      // if (!showTimer) return;
      if (secondsLeft <= 0) {
        submitQuestionAnswers?.();
        return;
      }
  
      const timer = setInterval(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
  
      return () => clearInterval(timer);
    }, [secondsLeft]);
  
    const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
    const seconds = String(secondsLeft % 60).padStart(2, "0");
  
    const isDanger = secondsLeft <= 30;

  const handlePrevious = () => {
    if (activeIndex === 0) return;

    setActiveIndex((prev) => prev - 1);
  };

  const handleBack = () => {
    setAllowLeave(true);
  }

  const handleTryAgain = () => {
    setOpen(false)
    setSelectedAnswers({})
    setActiveIndex(0)
    setActiveQuestion(0)
  }

  const handleClose = () => {
    setOpenSubmitModal(false);
    setActiveIndex(0)
    setActiveQuestion(0)
  }

  const currentQuestion = postAssessmentChapterQuestionData?.[activeIndex];

  const isCurrentQuestionAnswered =
    !!selectedAnswers[currentQuestion?.id];

  const handleQuestionAnswer = useCallback((question, option) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [question.id]: option.optionId,
    }));
  }, []);

  const handleContinue = () => {
  setAllowLeave(false)
  }

  const handleExit = () => {
    setAllowLeave(false);
    sessionStorage.removeItem(FINAL_ASSESSMENT_ALLOWED)
    navigate('/user/final-assessment')
    // submitQuestionAnswers();
  }

  useEffect(() => {
          // Add a dummy history entry
        window.history.pushState({ guard: true }, "", `${window.location.pathname}?${searchParams.toString()}`);
      
          const handlePopState = () => {
            if (!allowLeave) {
              setAllowLeave(true);
      
              // Re-push state to stop navigation
              window.history.pushState(
                { guard: true },
                "",
                window.location.pathname
              );
            }
          };
      
          window.addEventListener("popstate", handlePopState);
      
        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
  },[allowLeave])
  
  useEffect(() => {
  const allowed = sessionStorage.getItem(FINAL_ASSESSMENT_ALLOWED);

  if (!allowed) {
    navigate(
      `/user/final-assessment`,
      { replace: true }
    );
    return;
  }

  // Remove flag when tab/page is about to unload (refresh, close, etc.)
  const handleBeforeUnload = () => {
    submitQuestionAnswers()
    sessionStorage.removeItem(FINAL_ASSESSMENT_ALLOWED);
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
 }, []);

  useEffect(() => {
        const allowed = sessionStorage.getItem(FINAL_ASSESSMENT_ALLOWED);
      
        if (!allowed) {
          navigate(
            `/user/final-assessment`,
            {replace:true}
          );
        }
  }, []);

  const isLastQuestion =
  postAssessmentChapterQuestionData && activeIndex === postAssessmentChapterQuestionData.length - 1;

  return (
    <>
    <div className={styles.page}>
      <HeaderModule
        className={styles["header-title"]}
        onBackClick={handleBack}
        current={activeQuestion} 
        minutes={minutes}
        seconds={seconds}
        isDanger={isDanger}
        total={postAssessmentChapterQuestionData?.length}
      />
      <div className={styles["container-wrapper"]}>
        <div className={styles["content-part"]}>
         {isLoading || isFetching ?  <Shimmer width="90%" height="324px" borderRadius="14px" /> : 
         <CourseQuestionCards
          questionsList={postAssessmentChapterQuestionData}
          handleQuestionAnswer={handleQuestionAnswer}
          activeIndex={activeIndex}
          animate={animate}
          selectedAnswers={selectedAnswers}
          />
          }
        </div>
        <ToastContainer />
      </div>
      <div className={styles["bottom-wrapper"]}>
        <Button
          label={
                getTranslatedText(
                translatedText,
                "key_previous",
                "Previous",
              )}
          buttonVariant={BUTTON_VARIANTS.SECONDARY}
          className={styles["button-class"]}
          onClick={handlePrevious}
          disabled={activeIndex === 0 || activeIndex < 0}
          startIcon={
            <img
              className={styles["left-chevron"]}
              src={RightChevron}
              alt="chevron"
            />
          }
        />
        <Button
          label={isLastQuestion ? 
            getTranslatedText(
                translatedText,
                "submit_btn",
                'Submit',
            ) 
            : 
            getTranslatedText(
                translatedText,
                "next_txt",
                "Next",
            )}
          buttonVariant={BUTTON_VARIANTS.PRIMARY}
          className={styles["button-class"]}
          onClick={handleNext}
          disabled={!isCurrentQuestionAnswered || isLoading || isPending}
        />
      </div>
      <ToastContainer />
    </div>
    {open && <AssessmentResultModal handleTryAgain={handleTryAgain} />}
    {openSubmitModal && <SubmitModal loader={loader} onSubmit={submitQuestionAnswers} onClose={handleClose} />}
    {allowLeave && <ExitAssessmentModal onExit={handleExit} onContinue={handleContinue} />}
    </>
  );
};

export default FinalAssessmentQuestion;
